// react
import { forwardRef, useMemo } from 'react';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { Coral } from '@/theme/mui/color';

const CoralBase = Coral[600];

// mui
import { Box, IconButton } from '@mui/material';

// react-slick
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Slider from 'react-slick';

interface CarouselSlickItem {
  id: string;
  renderItem: React.ReactNode;
}

interface CarouselSlickProps {
  items: CarouselSlickItem[];
  title?: string;
  isLoading?: boolean;
  maxWidth?: string;
  initialSlide?: number;
  onSlideChange?: (index: number) => void;
}

// react-slick passes className, style, and onClick to custom arrow components
interface ArrowProps {
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const PrevArrow = forwardRef<HTMLButtonElement, ArrowProps>(
  ({ onClick }, ref) => (
    <IconButton
      ref={ref}
      aria-label='Previous'
      onClick={onClick}
      size='small'
      sx={{
        position: 'absolute',
        bottom: -34,
        left: 10,
        zIndex: 1,
        width: 24,
        height: 24,
      }}
    >
      <Icon icon='chevron-left' fontSize={12} />
    </IconButton>
  ),
);

const NextArrow = forwardRef<HTMLButtonElement, ArrowProps>(
  ({ onClick }, ref) => (
    <IconButton
      ref={ref}
      aria-label='Next'
      onClick={onClick}
      size='small'
      sx={{
        position: 'absolute',
        bottom: -34,
        right: 10,
        zIndex: 1,
        width: 24,
        height: 24,
      }}
    >
      <Icon icon='chevron-right' fontSize={12} />
    </IconButton>
  ),
);

/**
 * CarouselSlick displays a series of components on a swipable track.
 */
export const CarouselSlick = ({
  items,
  title,
  isLoading,
  maxWidth,
  initialSlide = 0,
  onSlideChange,
}: CarouselSlickProps) => {
  const settings = useMemo(
    () => ({
      dots: true,
      infinite: false,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide,
      afterChange: onSlideChange,
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
    }),
    [initialSlide, onSlideChange],
  );

  return (
    <Box
      sx={{
        paddingBottom: 7,
        maxWidth: maxWidth || '100%',
        overflow: 'hidden',
        '& .slick-list': { overflow: 'visible' },
        '& .slick-dots li': { width: 5 },

        '& .slick-dots li button:before': {
          color: CoralBase,
          fontSize: 10,
          top: 8,
        },

        '& .slick-dots li.slick-active button:before': {
          color: CoralBase,
        },
      }}
    >
      <Slider {...settings}>
        {items.map((item) => (
          <div key={item.id}>{item.renderItem}</div>
        ))}
      </Slider>
    </Box>
  );
};

CarouselSlick.displayName = 'CarouselSlick';

export default CarouselSlick;
